import { useCallback, useMemo, useReducer } from "react";
import { parseStructuredDocument } from "./structuredDocument";

const key = () => `stage-${crypto.randomUUID()}`;
const emailKey = () => `email-${crypto.randomUUID()}`;
export const emptyEmail = (order = 1) => ({
  localKey: emailKey(),
  id: null,
  order,
  name: "",
  subject: "",
  bodyHtml: "",
  bodyText: "",
  contentDocument: {},
  delayHours: (order - 1) * 24,
  isEnabled: false,
  ctaLabel: "",
  ctaUrl: "",
  utmSource: "fruitify",
  utmMedium: "email",
  utmCampaign: "",
  utmContent: "",
  useBrandedLayout: true,
  sender: null,
});

export const hydrateEditor = (workflow) => {
  const version = workflow?.draftVersion;
  const stages = [...(version?.stages || [])]
    .sort((a, b) => a.order - b.order)
    .map((stage) => ({
      ...stage,
      advanceAfterEmailSequence: Boolean(stage.advanceAfterEmailSequence),
      criterionConfig: parseStructuredDocument(stage.criterionConfig),
      templates: [...(stage.templates || [])]
        .sort((a, b) => a.order - b.order)
        .map((template) => ({
          ...template,
          localKey: `${stage.key}-${template.order}`,
        })),
    }));
  return {
    workflow: workflow
      ? {
          name: workflow.name || "",
          description: workflow.description || "",
          sendFirstEmailOnEnrollment: Boolean(
            workflow.sendFirstEmailOnEnrollment,
          ),
          sendWindowStart: workflow.sendWindowStart,
          sendWindowEnd: workflow.sendWindowEnd,
          sendTimezone: workflow.sendTimezone || "Europe/London",
          activeWeekdays: workflow.activeWeekdays || [0, 1, 2, 3, 4, 5, 6],
        }
      : null,
    stages,
    transitions: (version?.transitions || []).map((edge) => ({
      sourceKey: edge.sourceStage.key,
      targetKey: edge.targetStage.key,
      outcome: edge.outcome.toLowerCase(),
    })),
    selectedStageKey: stages[0]?.key || null,
    dirty: false,
  };
};

const normalize = (items) =>
  items.map((item, index) => ({ ...item, order: index + 1 }));

export const emailIntervalHours = (templates, index) => {
  const current = Number(templates[index]?.delayHours) || 0;
  if (index === 0) return current;
  return current - (Number(templates[index - 1]?.delayHours) || 0);
};

export const updateEmailInterval = (
  templates,
  emailKeyValue,
  intervalHours,
) => {
  const index = templates.findIndex(
    (email) => email.localKey === emailKeyValue,
  );
  if (index < 0) return templates;
  const nextInterval = Math.max(0, Number(intervalHours) || 0);
  const delta = nextInterval - emailIntervalHours(templates, index);
  return templates.map((email, emailIndex) =>
    emailIndex >= index
      ? { ...email, delayHours: Number(email.delayHours) + delta }
      : email,
  );
};

export const moveEmail = (templates, emailKeyValue, delta) => {
  const index = templates.findIndex(
    (email) => email.localKey === emailKeyValue,
  );
  const target = index + delta;
  if (index < 0 || target < 0 || target >= templates.length) return templates;
  const delaySlots = templates.map((email) => email.delayHours);
  const reordered = [...templates];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
  return normalize(
    reordered.map((email, emailIndex) => ({
      ...email,
      delayHours: delaySlots[emailIndex],
    })),
  );
};

export const removeEmail = (templates, emailKeyValue) => {
  const index = templates.findIndex(
    (email) => email.localKey === emailKeyValue,
  );
  if (index < 0) return templates;
  const removedInterval = emailIntervalHours(templates, index);
  return normalize(
    templates
      .filter((email) => email.localKey !== emailKeyValue)
      .map((email, emailIndex) =>
        emailIndex >= index
          ? { ...email, delayHours: email.delayHours - removedInterval }
          : email,
      ),
  );
};

export const appendEmail = (templates) => [
  ...templates,
  {
    ...emptyEmail(templates.length + 1),
    delayHours:
      (Number(templates.at(-1)?.delayHours) || 0) + (templates.length ? 24 : 0),
  },
];
function reducer(state, action) {
  const dirty = { ...state, dirty: true };
  switch (action.type) {
    case "hydrate":
      return hydrateEditor(action.workflow);
    case "select":
      return { ...state, selectedStageKey: action.key };
    case "workflow":
      return { ...dirty, workflow: { ...state.workflow, ...action.value } };
    case "addStage": {
      const stageKey = key();
      const stage = {
        key: stageKey,
        name: `Stage ${state.stages.length + 1}`,
        description: "",
        order: state.stages.length + 1,
        isEntry: state.stages.length === 0,
        isTerminal: false,
        advanceAfterEmailSequence: false,
        criterionCode: action.criterion?.code || "email_verified",
        criterionConfig: Object.fromEntries(
          (action.criterion?.options || []).map((o) => [o.key, o.default]),
        ),
        canvasX: 80 + state.stages.length * 260,
        canvasY: 100,
        templates: [emptyEmail()],
      };
      return {
        ...dirty,
        stages: [...state.stages, stage],
        selectedStageKey: stageKey,
      };
    }
    case "updateStage":
      return {
        ...dirty,
        stages: state.stages.map((stage) =>
          stage.key === action.key
            ? { ...stage, ...action.value }
            : action.value.isEntry
              ? { ...stage, isEntry: false }
              : stage,
        ),
      };
    case "deleteStage":
      return {
        ...dirty,
        stages: normalize(state.stages.filter((s) => s.key !== action.key)),
        transitions: state.transitions.filter(
          (e) => e.sourceKey !== action.key && e.targetKey !== action.key,
        ),
        selectedStageKey:
          state.selectedStageKey === action.key ? null : state.selectedStageKey,
      };
    case "moveStage": {
      const index = state.stages.findIndex((s) => s.key === action.key);
      const target = index + action.delta;
      if (target < 0 || target >= state.stages.length) return state;
      const stages = [...state.stages];
      [stages[index], stages[target]] = [stages[target], stages[index]];
      return { ...dirty, stages: normalize(stages) };
    }
    case "position":
      return {
        ...dirty,
        stages: state.stages.map((s) =>
          s.key === action.key
            ? {
                ...s,
                canvasX: Math.round(action.x),
                canvasY: Math.round(action.y),
              }
            : s,
        ),
      };
    case "connect":
      return {
        ...dirty,
        transitions: [
          ...state.transitions.filter((e) => e.sourceKey !== action.sourceKey),
          {
            sourceKey: action.sourceKey,
            targetKey: action.targetKey,
            outcome: action.outcome,
          },
        ],
      };
    case "disconnect":
      return {
        ...dirty,
        transitions: state.transitions.filter(
          (e) => e.sourceKey !== action.sourceKey,
        ),
      };
    case "addEmail":
      return {
        ...dirty,
        stages: state.stages.map((s) =>
          s.key === action.stageKey
            ? {
                ...s,
                templates: appendEmail(s.templates),
              }
            : s,
        ),
      };
    case "updateEmail":
      return {
        ...dirty,
        stages: state.stages.map((s) =>
          s.key === action.stageKey
            ? {
                ...s,
                templates: s.templates.map((e) =>
                  e.localKey === action.emailKey
                    ? { ...e, ...action.value }
                    : e,
                ),
              }
            : s,
        ),
      };
    case "updateEmailInterval":
      return {
        ...dirty,
        stages: state.stages.map((s) =>
          s.key === action.stageKey
            ? {
                ...s,
                templates: updateEmailInterval(
                  s.templates,
                  action.emailKey,
                  action.intervalHours,
                ),
              }
            : s,
        ),
      };
    case "deleteEmail":
      return {
        ...dirty,
        stages: state.stages.map((s) =>
          s.key === action.stageKey
            ? {
                ...s,
                templates: removeEmail(s.templates, action.emailKey),
              }
            : s,
        ),
      };
    case "moveEmail":
      return {
        ...dirty,
        stages: state.stages.map((s) => {
          if (s.key !== action.stageKey) return s;
          return {
            ...s,
            templates: moveEmail(s.templates, action.emailKey, action.delta),
          };
        }),
      };
    default:
      return state;
  }
}

const serializeEmail = (template) => {
  const email = { ...template, templateId: template.id || null };
  delete email.lineageKey;
  delete email.sourceLabel;
  delete email.localKey;
  delete email.id;
  delete email.sender;
  delete email.senderId;
  delete email.__typename;
  return email;
};

const serializeStage = (value) => {
  const stage = { ...value, templates: value.templates.map(serializeEmail) };
  delete stage.id;
  delete stage.__typename;
  return stage;
};

export const serializeEditor = (state) => {
  // A stage has a single progression outcome in the current editor. Keep the
  // newest connection if React Flow emits more than one connection event
  // before React has committed the reducer update.
  const transitionsBySource = new Map();
  state.transitions.forEach(({ sourceKey, targetKey, outcome }) => {
    transitionsBySource.set(sourceKey, {
      sourceKey,
      targetKey,
      outcome: outcome.toLowerCase(),
    });
  });
  return {
    settings: state.workflow,
    stages: state.stages.map(serializeStage),
    transitions: [...transitionsBySource.values()],
  };
};

export function useWorkflowEditor(workflow) {
  const [state, dispatch] = useReducer(reducer, workflow, hydrateEditor);
  const selectedStage =
    state.stages.find((s) => s.key === state.selectedStageKey) || null;
  const input = useMemo(() => serializeEditor(state), [state]);
  const hydrate = useCallback(
    (value) => dispatch({ type: "hydrate", workflow: value }),
    [],
  );
  return { state, selectedStage, input, dispatch, hydrate };
}

export const timingLabel = (hours) => {
  const totalHours = Math.max(0, Number(hours) || 0);
  if (totalHours === 0) return "On entering this stage";
  const days = Math.floor(totalHours / 24);
  const remainingHours = Number((totalHours % 24).toFixed(2));
  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? "" : "s"}`);
  if (remainingHours)
    parts.push(`${remainingHours} hour${remainingHours === 1 ? "" : "s"}`);
  return `${parts.join(" and ")} after entering`;
};

export const emailTimingLabel = (templates, index) => {
  const interval = emailIntervalHours(templates, index);
  if (index === 0) return timingLabel(interval);
  if (interval === 0) return "Immediately after previous email";
  return timingLabel(interval).replace(
    "after entering",
    "after previous email",
  );
};

export const stageCriterionUpdate = (criteria, code, isTerminal = false) => {
  const criterion = criteria.find((item) => item.code === code);
  return {
    criterionCode: code,
    criterionConfig: Object.fromEntries(
      (criterion?.options || []).map((option) => [option.key, option.default]),
    ),
    isTerminal: code === "terminal" ? true : isTerminal,
    ...(code === "terminal" ? { advanceAfterEmailSequence: false } : {}),
  };
};
